import React, { useCallback } from "react";
import {
  defaultDataTableTheme,
  DENSITY_STYLES,
  COLUMN_WIDTHS,
  PINNED_SHADOWS,
  TABLE_Z_INDEX,
  TABLE_BORDERS,
} from "../DataTable.theme";
import { TableCell } from "@mui/material";
import { motion } from "framer-motion";
import { DataTableColumn, FilterModel, FilterValue } from "../DataTable.types";
import {
  TextFilter,
  SelectFilter,
  MultiSelectFilter,
  DateFilter,
  DateRangeFilter,
  NumberRangeFilter,
} from "./filters";

interface FilterCellProps<TRow> {
  column: DataTableColumn<TRow>;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  density?: "compact" | "standard" | "comfortable";
}

/**
 * Filter cell component for inline filtering.
 * Renders appropriate filter input based on column's filterType.
 * Returns null if no filterType is defined (opt-in behavior).
 */
function FilterCellInput<TRow>({
  column,
  value,
  onChange,
  density = "standard",
}: FilterCellProps<TRow>): React.ReactElement | null {
  // If no filterType is defined, don't render any filter (opt-in behavior)
  if (!column.filterType) {
    return null;
  }

  const commonProps = {
    value,
    onChange,
    density,
    options: column.filterOptions,
  };

  switch (column.filterType) {
    case "text":
      return <TextFilter {...commonProps} />;

    case "select":
      return <SelectFilter {...commonProps} />;

    case "multi-select":
      return <MultiSelectFilter {...commonProps} />;

    case "custom":
      if (column.filterComponent) {
        const CustomFilter = column.filterComponent;
        return <CustomFilter {...commonProps} />;
      }
      return null;

    case "date":
      return <DateFilter {...commonProps} />;

    case "date-range":
      return <DateRangeFilter {...commonProps} />;

    case "number-range":
      return <NumberRangeFilter {...commonProps} />;

    default:
      return null;
  }
}

interface FilterRowProps<TRow> {
  columns: DataTableColumn<TRow>[];
  filterModel: FilterModel;
  onFilterModelChange: (model: FilterModel) => void;
  density?: "compact" | "standard" | "comfortable";
  hasSelectable?: boolean;
  selectionColumnWidth?: number;
  hasRowControls?: boolean;
  rowControlsWidth?: number;
  hasExpandable?: boolean;
  /** Get column width - must match the same function used in header/body */
  getColumnWidth: (
    column: DataTableColumn<TRow>,
  ) => number | string | undefined;
  /** Pinned columns for calculating sticky offsets */
  pinnedLeft?: DataTableColumn<TRow>[];
  pinnedRight?: DataTableColumn<TRow>[];
  /** Functions to calculate sticky offsets - same as header uses */
  getPinnedLeftOffset?: (index: number) => number;
  getPinnedRightOffset?: (index: number) => number;
  /** Show vertical dividers between columns */
  showColumnDividers?: boolean;
  /** Scrolling state for shadows */
  isScrolledLeft?: boolean;
  isScrolledRight?: boolean;
  isResizing?: boolean;
  lastPinnedLeftKey?: string | null;
  firstPinnedRightKey?: string | null;
}

const MotionTableCell = motion.create(TableCell);

/**
 * Filter row component that renders a row of filter inputs.
 * Uses TableCell to match header/body alignment.
 */
export function FilterRow<TRow>({
  columns,
  filterModel,
  onFilterModelChange,
  density = "standard",
  hasSelectable = false,
  selectionColumnWidth = COLUMN_WIDTHS.checkbox +
    COLUMN_WIDTHS.firstColumnPadding,
  hasRowControls = false,
  rowControlsWidth = 0,
  hasExpandable = false,
  getColumnWidth,
  pinnedLeft = [],
  pinnedRight = [],
  getPinnedLeftOffset,
  getPinnedRightOffset,
  showColumnDividers = false,
  isScrolledLeft = false,
  isScrolledRight = false,
  isResizing = false,
  lastPinnedLeftKey = null,
  firstPinnedRightKey = null,
}: FilterRowProps<TRow>): React.ReactElement {
  const handleFilterChange = useCallback(
    (columnKey: string, value: FilterValue) => {
      const newModel = { ...filterModel };
      if (value === null) {
        delete newModel[columnKey];
      } else {
        newModel[columnKey] = value;
      }
      onFilterModelChange(newModel);
    },
    [filterModel, onFilterModelChange],
  );

  // Density styles (from centralized theme)
  const densityPadding = DENSITY_STYLES[density].padding;

  const cellStyle = {
    padding: densityPadding,
    backgroundColor: defaultDataTableTheme.headerBg,
    borderBottom: `${TABLE_BORDERS.emphasis} solid ${defaultDataTableTheme.borderColor}`,
    transition: "box-shadow 0.3s ease",
  };
  const selectionColumnPadding = Math.max(
    0,
    selectionColumnWidth - COLUMN_WIDTHS.checkbox,
  );
  return (
    <>
      {/* Empty cell for selection checkbox column */}
      {hasSelectable && (
        <MotionTableCell
          layout={!isResizing}
          transition={
            isResizing
              ? { duration: 0 }
              : {
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }
          }
          key="__select__"
          padding="none"
          sx={{
            ...cellStyle,
            boxSizing: "border-box",
            width: selectionColumnWidth,
            minWidth: selectionColumnWidth,
            maxWidth: selectionColumnWidth,
            padding: `0 0 0 ${selectionColumnPadding}px`,
            overflow: "hidden",
            textOverflow: "clip",
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            zIndex: TABLE_Z_INDEX.pinnedHeader,
            ...(isScrolledLeft &&
              lastPinnedLeftKey === "__select__" && {
                boxShadow: PINNED_SHADOWS.left,
              }),
          }}
        />
      )}

      {/* Empty cell for structural row controls */}
      {hasRowControls && (
        <MotionTableCell
          layout={!isResizing}
          transition={
            isResizing
              ? { duration: 0 }
              : {
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }
          }
          key="__row_controls__"
          padding="none"
          sx={{
            ...cellStyle,
            boxSizing: "border-box",
            width: rowControlsWidth,
            minWidth: rowControlsWidth,
            maxWidth: rowControlsWidth,
            padding: 0,
            overflow: "hidden",
            textOverflow: "clip",
            whiteSpace: "nowrap",
            position: "sticky",
            left: hasSelectable ? selectionColumnWidth : 0,
            zIndex: TABLE_Z_INDEX.pinnedHeader,
            ...(isScrolledLeft &&
              lastPinnedLeftKey === "__row_controls__" && {
                boxShadow: PINNED_SHADOWS.left,
              }),
          }}
        />
      )}

      {/* Empty cell for expand column */}
      {hasExpandable && (
        <MotionTableCell
          layout={!isResizing}
          transition={
            isResizing
              ? { duration: 0 }
              : {
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }
          }
          key="__expand__"
          padding="none"
          sx={{
            ...cellStyle,
            boxSizing: "border-box",
            width: COLUMN_WIDTHS.checkbox,
            minWidth: COLUMN_WIDTHS.checkbox,
            maxWidth: COLUMN_WIDTHS.checkbox,
            padding: 0, // Force no padding
            overflow: "hidden",
            textOverflow: "clip",
            whiteSpace: "nowrap",
            position: "sticky",
            left: (hasSelectable ? selectionColumnWidth : 0) + rowControlsWidth,
            zIndex: TABLE_Z_INDEX.pinnedHeader,
            ...(isScrolledLeft &&
              lastPinnedLeftKey === "__expand__" && {
                boxShadow: PINNED_SHADOWS.left,
              }),
          }}
        />
      )}

      {/* Filter cells for each visible column */}
      {columns
        .filter((col) => !col.hidden)
        .map((column, colIndex) => {
          // Calculate sticky positioning for pinned columns
          const isPinnedLeft = column.pinned === "left";
          const isPinnedRight = column.pinned === "right";

          let stickyStyle = {};
          if (isPinnedLeft && getPinnedLeftOffset) {
            const index = pinnedLeft.findIndex((c) => c.key === column.key);
            stickyStyle = {
              position: "sticky" as const,
              left: getPinnedLeftOffset(index),
              zIndex: TABLE_Z_INDEX.pinnedHeader,
            };
          } else if (isPinnedRight && getPinnedRightOffset) {
            const index = pinnedRight.findIndex((c) => c.key === column.key);
            stickyStyle = {
              position: "sticky" as const,
              right: getPinnedRightOffset(index),
              zIndex: TABLE_Z_INDEX.pinnedHeader,
              ...(isScrolledRight &&
                firstPinnedRightKey === column.key && {
                  boxShadow: PINNED_SHADOWS.right,
                }),
              ...(isPinnedLeft &&
                isScrolledLeft &&
                lastPinnedLeftKey === column.key && {
                  boxShadow: PINNED_SHADOWS.left,
                }),
            };
          }

          // Add border-left for data columns when dividers enabled
          // Using colIndex >= 0 ensures the first data column has a divider on its left (between it and selection/expand column)
          const dividerStyle =
            showColumnDividers && colIndex >= 0
              ? { borderLeft: "1px solid var(--mela-border-default)" }
              : {};

          return (
            <MotionTableCell
              layout={!isResizing}
              transition={
                isResizing
                  ? { duration: 0 }
                  : {
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                    }
              }
              key={column.key}
              sx={{
                ...cellStyle,
                width: getColumnWidth(column),
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                ...stickyStyle,
                ...dividerStyle,
              }}
            >
              <FilterCellInput
                column={column}
                value={filterModel[column.key] || null}
                onChange={(value) => handleFilterChange(column.key, value)}
                density={density}
              />
            </MotionTableCell>
          );
        })}
    </>
  );
}
